import mixpanel from 'mixpanel-browser';
import { useState } from 'react';
import { TrackableEvents } from '../Components/Tracked/events';
import { Moment } from 'moment';
import { ReportType } from '../Constants/HistoricalReportConstants';

export interface ReportFileNameState {
  name: string;
  isEditable: boolean;
}

const REPORT_TYPE_NAME: Record<string, string> = {
  ['Weather Conditions']: 'WeatherData',
  ['Policy Delays']: 'PolicyDelays',
};

export const useReportFileName = () => {
  const [fileNameState, setFileNameState] = useState<ReportFileNameState>({ name: '', isEditable: true });

  const buildReportName = (
    reportType: ReportType,
    optionsMap: Map<number | string, string>,
    selections: number[] | string[],
    startDate: moment.Moment | null,
    endDate: moment.Moment | null
  ) => {
    const reportTypeName = REPORT_TYPE_NAME[reportType];

    let startDateString = null;
    let endDateString = null;

    if (startDate && endDate) {
      if (startDate.year() === endDate.year()) {
        startDateString = startDate.format('MM-DD');
        endDateString = endDate.format('MM-DD-YY');
      } else {
        startDateString = startDate.format('MM-DD-YY');
        endDateString = endDate.format('MM-DD-YY');
      }
    }

    let reportName = '';
    if (selections.length === 1) {
      const selectionName = optionsMap.get(selections[0].toString()) ?? '';
      reportName = `${selectionName}_${reportTypeName}`.replace(' ', '_');
    } else if (selections.length > 1) {
      reportName = 'MultiLocation_WeatherData';
    }

    if (startDateString && endDateString) {
      reportName += `_${startDateString}_${endDateString}`;
    }

    return reportName;
  };

  const getReportName = (
    reportType: ReportType,
    optionsMap: Map<number | string, string>,
    selections: number[] | string[],
    startDate: Moment | null,
    endDate: Moment | null
  ) => {
    if (!fileNameState.isEditable) {
      return fileNameState.name;
    }

    return buildReportName(reportType, optionsMap, selections, startDate, endDate);
  };

  const handleChangeFileName = (newFileName: string) => {
    mixpanel.track(TrackableEvents.ButtonClicked, {
      button_location: 'Historical Report',
      button_feature: 'Historical Export',
      button_label: 'Rename',
      button_type: 'input',
    });

    setFileNameState({ name: newFileName, isEditable: false });
  };

  const updateFileNameState = (updates: Partial<ReportFileNameState>) => {
    setFileNameState(prev => ({ ...prev, ...updates }));
  };

  return {
    fileNameState,
    getReportName,
    buildReportName,
    handleChangeFileName,
    updateFileNameState,
  };
};
